import React, { useState, useEffect, useMemo } from 'react';
import './CardDirectory.scss';

const FILTER_CONFIG = {
  class: { label: 'Class', options: ['Ironclad', 'Silent', 'Defect', 'Watcher', 'Colorless'] },
  type: { label: 'Type', options: ['Attack', 'Skill', 'Power'] },
  cost: { label: 'Cost', options: ['0', '1', '2', '3+', 'X', 'Unplayable'] },
};

const getDefaultFilters = () => {
  const defaults = {};
  for (const key in FILTER_CONFIG) {
    defaults[key] = [...FILTER_CONFIG[key].options];
  }
  return defaults;
};

const CardDirectory = ({ allCards, guessedCards, show, onClose, onCardSelect, sessionStorageKey = 'stsCardDirectoryFiltersV2' }) => {
  const [filters, setFilters] = useState(() => {
    try {
      const storedFilters = sessionStorage.getItem(sessionStorageKey);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);
        let isValid = true;
        for (const key in FILTER_CONFIG) {
          if (!parsedFilters[key] || !Array.isArray(parsedFilters[key])) {
            isValid = false;
            break;
          }
          parsedFilters[key] = parsedFilters[key].filter(opt => FILTER_CONFIG[key].options.includes(opt));
        }
        if (isValid) return parsedFilters;
      }
    } catch (error) {
      console.error("Error reading filters from session storage:", error);
    }
    return getDefaultFilters();
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(filters));
    } catch (error) {
      console.error("Error saving filters to session storage:", error);
    }
  }, [filters, sessionStorageKey]);

  const handleFilterChange = (categoryKey, option, isRightClick = false) => {
    setFilters(prevFilters => {
      if (isRightClick) {
        // Right click: Turn off all options in this category except the clicked one
        // and ensure the clicked one is ON
        return {
          ...prevFilters,
          [categoryKey]: [option],
        };
      } else {
        // Left click: Toggle the clicked option on/off
        const currentCategorySelections = prevFilters[categoryKey] || [];
        const newCategorySelections = currentCategorySelections.includes(option)
          ? currentCategorySelections.filter(item => item !== option)
          : [...currentCategorySelections, option];
        
        return {
          ...prevFilters,
          [categoryKey]: newCategorySelections,
        };
      }
    });
  };

  const handleResetAllFilters = () => {
    setFilters(getDefaultFilters());
  };

  const filteredAndSortedCards = useMemo(() => {
    if (!allCards || allCards.length === 0) return [];

    return allCards
      .filter(card => {
        for (const categoryKey in filters) {
          const selectedOptions = filters[categoryKey];

          if (FILTER_CONFIG[categoryKey] && FILTER_CONFIG[categoryKey].options.length > 0 && selectedOptions.length === 0) {
            return false; 
          }
          
          if (FILTER_CONFIG[categoryKey] && selectedOptions.length > 0) {
            let cardMatchesCategory = false;
            const cardValue = card[categoryKey];

            if (categoryKey === 'cost') {
                let normalizedCardCost = card.cost;
                if (String(card.cost).toUpperCase() === 'X') normalizedCardCost = 'X';
                else if (card.cost === null) normalizedCardCost = 'Unplayable';
                else normalizedCardCost = parseInt(card.cost, 10);

                for (const costOption of selectedOptions) {
                    if (costOption === '0' && normalizedCardCost === 0) {
                        cardMatchesCategory = true; break;
                    }
                    if (costOption === '1' && normalizedCardCost === 1) {
                        cardMatchesCategory = true; break;
                    }
                    if (costOption === '2' && normalizedCardCost === 2) {
                        cardMatchesCategory = true; break;
                    }
                    if (costOption === '3+' && typeof normalizedCardCost === 'number' && normalizedCardCost >= 3) {
                        cardMatchesCategory = true; break;
                    }
                    if (costOption === 'X' && normalizedCardCost === 'X') {
                        cardMatchesCategory = true; break;
                    }
                    if (costOption === 'Unplayable' && normalizedCardCost === 'Unplayable') {
                        cardMatchesCategory = true; break;
                    }
                }
            } else {
                if (selectedOptions.includes(String(cardValue))) {
                    cardMatchesCategory = true;
                }
            }
            if (!cardMatchesCategory) return false;
          }
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCards, filters]);

  if (!show) {
    return null;
  }

  const handleCardClick = (cardName) => {
    if (onCardSelect) {
      onCardSelect(cardName);
    }
    onClose();
  };

  const areFiltersDefault = () => {
    const defaultFilters = getDefaultFilters();
    for (const categoryKey in filters) {
        if (!FILTER_CONFIG[categoryKey]) continue;
        const selectedForCategory = filters[categoryKey]?.sort().join(',') || '';
        const defaultForCategory = defaultFilters[categoryKey]?.sort().join(',') || '';
        if (selectedForCategory !== defaultForCategory) {
            return false;
        }
    }
    return true;
  };
  const isAnyFilterActive = !areFiltersDefault();

  return (
    <div className="card-directory__overlay" onClick={onClose}>
      <div className="card-directory__panel" onClick={(e) => e.stopPropagation()}>
        <button className="card-directory__close-button" onClick={onClose}>✕</button>
        <div className="card-directory__header">
            <h2>Card Directory</h2>
            {isAnyFilterActive && (
                <button onClick={handleResetAllFilters} className="card-directory__reset-button">
                    Reset Filters
                </button>
            )}
        </div>

        <div className="card-directory__filters">
          {Object.entries(FILTER_CONFIG).map(([categoryKey, config]) => (
            <div key={categoryKey} className="card-directory__filter-category">
              <span className="card-directory__filter-label">{config.label}:</span>
              <div className="card-directory__filter-options">
                {config.options.map(option => (
                  <button
                    key={option}
                    className={`card-directory__filter-button ${
                      filters[categoryKey]?.includes(option) ? 'card-directory__filter-button--selected' : ''
                    }`}
                    onClick={() => handleFilterChange(categoryKey, option, false)}
                    onContextMenu={(e) => {
                      e.preventDefault(); // Prevent default context menu
                      handleFilterChange(categoryKey, option, true);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <ul className="card-directory__list">
          {filteredAndSortedCards.length > 0 ? (
            filteredAndSortedCards.map((card) => (
              <li
                key={`${card.name}-${card.class}`}
                className={`card-directory__list-item ${
                  guessedCards.includes(card.name) ? 'card-directory__list-item--guessed' : ''
                }`}
                onClick={() => {
                  if (!guessedCards.includes(card.name)) {
                    handleCardClick(card.name);
                  }
                }}
                style={!guessedCards.includes(card.name) ? { cursor: 'pointer' } : {}}
              >
                <div className="card-directory__item-content">
                  <span className="card-directory__item-name">{card.name}</span>
                  <div className="card-directory__item-details">
                    <span className="card-directory__item-detail">{card.class}</span>
                    <span className="card-directory__item-detail">{card.type}</span>
                    <span className="card-directory__item-detail">
                      {String(card.cost).toUpperCase() === 'X' ? 'X' : 
                       card.cost === null ? 'Unplayable' : 
                       String(card.cost)}
                    </span>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="card-directory__list-item card-directory__list-item--empty">No cards match the current filters.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default CardDirectory;